import { useEffect, useRef, useState } from 'react';

export interface UseBannerOptions {
  /** Whether this banner has been triggered at all (a query param matched,
   * a date range, whatever the specific banner decides). Everything below
   * only runs once this is true. */
  active: boolean;
  /** ms of inactivity before the reveal plays itself. Omit (or 0) to
   * require a manual trigger (e.g. a click) instead. */
  autoStartDelay?: number;
  /** ms after landing before it tucks itself back into the minimized
   * pill. Omit (or 0) to never auto-minimize. */
  autoMinimizeDelay?: number;
}

/**
 * The state machine every banner shares, regardless of what its own reveal
 * animation looks like: idle -> started -> landed -> minimized, plus the
 * behaviors around that lifecycle (auto-start, auto-minimize, pausing the
 * countdown on hover, and minimizing early on an outside click).
 *
 * A specific banner owns its own reveal animation and decides when to call
 * `setLanded(true)` -- this hook only manages the states around that and
 * the DOM listeners tied to them. Spread `containerProps` onto the
 * banner's root element and use `sceneRef` as that same element's ref, so
 * the outside-click detection knows what counts as "inside."
 */
export function useBanner({ active, autoStartDelay, autoMinimizeDelay }: UseBannerOptions) {
  const sceneRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [landed, setLanded] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [paused, setPaused] = useState(false);
  const expanded = landed && !minimized;

  useEffect(() => {
    if (!active || started || !autoStartDelay) return;
    const timer = setTimeout(() => setStarted(true), autoStartDelay);
    return () => clearTimeout(timer);
  }, [active, started, autoStartDelay]);

  useEffect(() => {
    if (!landed || minimized || paused || !autoMinimizeDelay) return;
    const timer = setTimeout(() => setMinimized(true), autoMinimizeDelay);
    return () => clearTimeout(timer);
  }, [landed, minimized, paused, autoMinimizeDelay]);

  // Clicking anywhere else on the page minimizes it early, the same way
  // the countdown above eventually would on its own. Only attached while
  // expanded -- the click that reveals a banner happens before this ever
  // mounts, and there's nothing to minimize once it already is.
  useEffect(() => {
    if (!expanded) return;
    function handlePointerDown(event: PointerEvent) {
      if (!sceneRef.current?.contains(event.target as Node)) {
        setMinimized(true);
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [expanded]);

  return {
    sceneRef,
    started,
    setStarted,
    landed,
    setLanded,
    minimized,
    setMinimized,
    paused,
    expanded,
    containerProps: {
      onMouseEnter: () => setPaused(true),
      onMouseLeave: () => setPaused(false),
      onTouchStart: () => setPaused(true),
    },
  };
}
